import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { feedbackHref } from "../../lib/feedbackLink";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { Clock, Info } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Label, scoreColor, InfoBadge } from "../../components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import RatingsCollected7d from "../../components/RatingsCollected7d";

export default function Analytics() {
  const { user } = useAuth();
  const isCorp = user.role === "corporate_admin";
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState("all");
  const [peak, setPeak] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { if (isCorp) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, [isCorp]);
  useEffect(() => {
    const q = locId !== "all" ? `?location_id=${locId}` : "";
    api.get(`/analytics/peak-hours${q}`).then((r) => setPeak(r.data)).catch(() => {});
  }, [locId]);
  useEffect(() => { api.get("/analytics/gap-matrix").then((r) => setMatrix(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><span className="flex items-center gap-1.5"><Label>Service Intelligence</Label>
          <InfoBadge title="Shift & Gap Analytics" testId="analytics-header-info"
            text="Shows when service is strongest or weakest across the day and week, and which parts of the customer interaction each location struggles with — so you can staff and coach at the right times." /></span>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Shift & Gap Analytics</h1></div>
        {isCorp && (
          <Select value={locId} onValueChange={setLocId}>
            <SelectTrigger className="w-48" data-testid="analytics-location"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All locations</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {peak?.insight && (
        <div className="flex items-start gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4" data-testid="peak-insight">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-600" />
          <p className="text-sm text-indigo-900">{peak.insight} <span className="text-indigo-500">(association, not causation — correlate with staffing and foot-traffic before acting.)</span></p>
        </div>
      )}

      <RatingsCollected7d testId="analytics-ratings-7d" />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2">
          <div className="flex items-center justify-between"><span className="flex items-center gap-1.5"><Label>Service Index by Hour</Label>
            <InfoBadge title="Service Index by Hour" testId="peak-hours-info"
              text="Average service score for each trading hour. Tall green bars are your best hours; short red bars flag times when customers rate service lower — often peak-hour pressure." /></span>
            <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> trading hours</span></div>
          <div className="mt-3" style={{ width: "100%", height: 260 }} data-testid="peak-hours-chart">
            {peak && (
              <ResponsiveContainer>
                <BarChart data={peak.hours.map((h) => ({ ...h, index: h.index ?? 0 }))} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94A3B8" }} interval={1} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} width={40} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => [`${v}`, "Index"]} />
                  <Bar dataKey="index" radius={[4, 4, 0, 0]}>
                    {peak.hours.map((h) => <Cell key={h.hour} fill={h.index ? scoreColor(h.index / 20) : "#E2E8F0"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <span className="flex items-center gap-1.5"><Label>By Day of Week</Label>
            <InfoBadge title="By Day of Week" testId="by-day-info"
              text="How service scores compare across the seven days. Use it to spot which days consistently run hot or cold and plan your strongest staff accordingly." /></span>
          <div className="mt-3 space-y-2">
            {peak?.days.map((d) => (
              <div key={d.day} className="flex items-center gap-3">
                <span className="w-10 text-xs font-bold uppercase text-slate-400">{d.day}</span>
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full" style={{ width: `${d.index || 0}%`, background: d.index ? scoreColor(d.index / 20) : "#E2E8F0" }} />
                </div>
                <span className="sv-metric w-10 text-right text-sm font-bold text-slate-700">{d.index ?? "—"}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <span className="flex items-center gap-1.5"><Label>Behavioural Gap Matrix</Label>
          <InfoBadge title="Behavioural Gap Matrix" testId="gap-matrix-info"
            text="A grid showing how well each location does on every part of the customer interaction (welcome, understanding, explaining, resolving). Red cells are the weakest moments — your clearest coaching targets." /></span>
        <p className="mt-1 text-sm text-slate-500">% positive per touchpoint across locations. Red cells are the weakest touchpoints.</p>
        <div className="mt-3 overflow-x-auto sv-scroll">
          <table className="w-full min-w-[640px] text-sm" data-testid="gap-matrix-table">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2">Location</th><th>Index</th>
              {matrix && Object.values(matrix.touchpoint_labels).map((l) => <th key={l} className="text-center">{l}</th>)}
              <th>Primary gap</th>
            </tr></thead>
            <tbody>
              {matrix?.rows.map((r) => (
                <tr key={r.location_id} className="border-b border-slate-100" data-testid={`gap-row-${r.location_id}`}>
                  <td className="py-3 font-semibold text-slate-800"><button className="hover:text-indigo-700 hover:underline" onClick={() => navigate(`/app/store?location=${r.location_id}`)} data-testid={`gap-loc-link-${r.location_id}`}>{r.location}</button></td>
                  <td><span className="sv-metric font-bold" style={{ color: scoreColor(r.ssi) }}>{r.ssi.toFixed(1)}</span></td>
                  {Object.keys(matrix.touchpoint_labels).map((k) => {
                    const v = r.touchpoints[k];
                    return <td key={k} className="text-center">
                      {v == null ? <span className="text-slate-300">—</span> :
                        <button onClick={() => navigate(feedbackHref({ location_id: r.location_id, touchpoint: k, answer: "no" }))} title="See ratings where this was missed" data-testid={`gap-cell-${r.location_id}-${k}`} className="inline-block rounded px-2 py-1 font-semibold transition-opacity hover:opacity-80" style={{ color: v >= 85 ? "#065F46" : v >= 70 ? "#92400E" : "#991B1B", background: v >= 85 ? "#ECFDF5" : v >= 70 ? "#FEF3C7" : "#FEE2E2" }}>{v}%</button>}
                    </td>;
                  })}
                  <td className="text-slate-500">{r.gap?.gap_label || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
